"use client";

import {
  selectIsAuthSuccessOrPerpetual,
  setIsCommonSsoModalVisible,
  store,
  useAppSelector,
} from "@repo/web-store";
import { FC, useCallback, useState } from "react";
import { CHAR_LIMITS, textEllipsis } from "../ClpStockCards/clpCardConstant";
import { PriceChange } from "../ClpStockCards/PriceChange";
import { PriceDisplay } from "../ClpStockCards/PriceDisplay";
import { WatchlistData } from "../ClpStockCards/types";
import WatchlistModal from "../Watchlist/WatchlistModal";
import type { SelectedStock } from "../Watchlist/types";
import { RecentlyViewedStockItem } from "./recentlyViewedTypes";

interface RecentlyViewedContentProps {
  stock: RecentlyViewedStockItem;
  onModalStateChange?: (isOpen: boolean) => void;
  modalTitle?: string;
  modalBtnCta?: string;
  successMsg?: string;
  errorMsg?: string;
}

export const RecentlyViewedContent: FC<RecentlyViewedContentProps> = ({
  stock,
  onModalStateChange,
  modalTitle,
  modalBtnCta,
  successMsg,
  errorMsg,
}) => {
  const { perChange, fullName, ltp, changePrice } = stock;
  const isPositive = Number(perChange) >= 0;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedWatchlistData, setSelectedWatchlistData] = useState<WatchlistData | null>(null);
  const loginStatus = useAppSelector(selectIsAuthSuccessOrPerpetual);

  const handleBookmarkClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      if (!loginStatus) {
        store.dispatch(setIsCommonSsoModalVisible(true));
        return;
      }

      setIsModalOpen(true);
      onModalStateChange?.(true);
    },
    [loginStatus, onModalStateChange],
  );

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    if (selectedWatchlistData) {
      setSelectedWatchlistData(null);
    }
    onModalStateChange?.(false);
  }, [onModalStateChange, selectedWatchlistData]);

  const selectedStock: SelectedStock = {
    exchange: stock.exchange ?? 0,
    token: stock.token ?? 0,
    symbol: stock.symbol,
    fullName: stock.fullName,
  };

  return (
    <div className="flex w-full min-w-0 flex-col gap-3">
      <div className="flex min-w-0 items-center justify-between gap-2">
        <h3 className="min-w-0 flex-1 truncate text-[14px] font-normal leading-[1.2] text-[#1a1a1a] md:text-[16px]">
          {textEllipsis(
            fullName || stock.symbol || "stock",
            CHAR_LIMITS.STOCK_TITLE,
            "recently-viewed-stock-name",
          )}
        </h3>
        <button
          type="button"
          onClick={handleBookmarkClick}
          className="shrink-0 rounded"
          aria-label="Add to watchlist"
        >
          <span className="flex h-4 w-4 cursor-pointer items-center justify-center transition-opacity hover:opacity-70 md:h-5 md:w-5">
            <i className="icomoon text-[16px] before:content-['\e95C'] md:text-[20px]" />
          </span>
        </button>
      </div>

      <div className="flex min-w-0 flex-col gap-1">
        <PriceDisplay ltp={ltp} isPositive={isPositive} />
        <PriceChange
          changePrice={changePrice}
          perChange={perChange}
          className="text-[12px] md:text-[14px]"
        />
      </div>

      {modalTitle && (
        <WatchlistModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          selectedStock={selectedStock}
          successMsg={successMsg}
          errorMsg={errorMsg}
          modalTitle={modalTitle}
          modalBtnCta={modalBtnCta}
          onWatchlistSelect={setSelectedWatchlistData}
        />
      )}
    </div>
  );
};
